import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "@/config/branding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F5F7' }} data-testid="forgot-password-page">
      <div className="bg-white/95 backdrop-blur-md border-b border-[#E5E5EA] px-6 py-3">
        <img src={LOGO_URL} alt="Self Certify UK" className="h-12 sm:h-14 object-contain object-left" />
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-fade-in-up">
          {sent ? (
            <div className="bg-white rounded-2xl p-10 text-center" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }} data-testid="reset-email-sent">
              <div className="w-16 h-16 bg-[#EAFBF0] flex items-center justify-center rounded-full mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-[#22C55E]" />
              </div>
              <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>
                Check Your Email
              </h2>
              <p className="text-sm mb-6" style={{ color: '#8E8E93' }}>
                If an account exists with <strong style={{ color: '#1C1C1E' }}>{email}</strong>, we've sent a password reset link. Check your inbox and spam folder.
              </p>
              <p className="text-xs mb-8" style={{ color: '#C7C7CC' }}>The link expires in 1 hour.</p>
              <Link to="/login" className="bg-[#00A3E0] text-white font-semibold rounded-xl px-6 py-2.5 inline-flex items-center justify-center gap-2 hover:bg-[#0082B3] transition-all w-full" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <ArrowLeft className="w-4 h-4" /> Back to Sign In
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 sm:p-10" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }} data-testid="forgot-password-form">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>
                Forgot Password
              </h2>
              <p className="text-sm mb-8" style={{ color: '#8E8E93' }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>
                    Email Address
                  </label>
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="brutalist-input"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <button
                  data-testid="forgot-submit-button"
                  type="submit"
                  disabled={loading}
                  className="brutalist-btn w-full flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#00A3E0' }}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>
              </form>

              <p className="mt-6 text-sm text-center" style={{ color: '#8E8E93' }}>
                <Link to="/login" className="text-[#00A3E0] font-semibold hover:underline" data-testid="back-to-login">
                  Back to Sign In
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
